import { ArrowLeftIcon, ArrowRightIcon, XIcon } from '@phosphor-icons/react'
import { useEffect } from 'react'
import { Dialog, DialogContent, DialogTitle } from 'app/components/ui/dialog'
import { NavArrow } from './NavArrow'
import { ResponseActions, ResponseDetail } from './ResponseDetail'
import { CHANNEL_LABELS } from './responseQueue'
import { displayName, stamp } from './ResponseRow'
import { ThreadExpansion } from './ThreadExpansion'
import { ResponseRow, threadParty } from './useResponseQueue'

export function ResponseRecordModal({
  row,
  ordered,
  onClose,
  onGo,
  onDone,
  onSnooze,
  onRead,
  onOpenContact,
}: {
  row?: ResponseRow
  ordered: ResponseRow[]
  onClose: () => void
  onGo: (rowId: string) => void
  onDone: (row: ResponseRow) => void
  onSnooze: (row: ResponseRow, until: Date) => void
  onRead: (row: ResponseRow) => void
  onOpenContact: (row: ResponseRow) => void
}) {
  const index = row ? ordered.findIndex((item) => item.id === row.id) : -1
  const party = row ? threadParty(row) : undefined
  const previous = index > 0 ? ordered[index - 1] : undefined
  const next = index >= 0 && index < ordered.length - 1 ? ordered[index + 1] : undefined

  // Arrow keys walk the queue, except while the caret is in anything typable.
  useEffect(() => {
    if (!row) return
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (target?.isContentEditable || [`INPUT`, `TEXTAREA`].includes(target?.tagName ?? ``)) return
      if (event.key === `ArrowRight` && next) onGo(next.id)
      if (event.key === `ArrowLeft` && previous) onGo(previous.id)
    }
    window.addEventListener(`keydown`, onKey)
    return () => window.removeEventListener(`keydown`, onKey)
  }, [row, next, previous, onGo])

  if (!row) return null

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        showClose={false}
        className="flex max-w-none items-center justify-center gap-4 border-0 bg-transparent p-0 shadow-none outline-none sm:max-w-none lg:gap-8"
      >
        <NavArrow direction="prev" onClick={() => previous && onGo(previous.id)} disabled={!previous} />

        {/* Keyed on the row so walking the queue remounts the card. */}
        <div key={row.id} className="flex w-[42rem] max-w-[calc(100vw-3rem)] flex-col">
          <div className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-default bg-surface-raised shadow-elevation-4">
            <div className="flex shrink-0 items-start justify-between gap-4 px-6 pb-1 pt-5">
              <div className="min-w-0">
                <DialogTitle className="gilroy truncate text-strong">{displayName(row)}</DialogTitle>
                <p className="mt-0.5 text-body-sm text-subtle">
                  {CHANNEL_LABELS[row.channel]} · {stamp(row.since)}
                  {index >= 0 && (
                    <span className="tabular-nums">
                      {` `}· {index + 1} of {ordered.length}
                    </span>
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                title="Close"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-subtle transition hover:bg-surface-muted hover:text-strong"
              >
                <XIcon size={16} weight="bold" />
              </button>
            </div>

            {/* Capped in viewport units: the card takes its height from its content,
                and a flex-1 child with no definite parent height overflows instead
                of scrolling. */}
            <div className="min-h-0 max-h-[55vh] overflow-y-auto pb-2">
              {row.channel === `sms` && party ? (
                <ThreadExpansion contact={party} onClose={onClose} onRead={() => onRead(row)} />
              ) : (
                // px-2 plus the record's own px-4 lines its edges up with the header's px-6.
                <div className="px-2">
                  <ResponseDetail row={row} />
                </div>
              )}
            </div>
          </div>

          <div className="mt-4 shrink-0">
            <ResponseActions
              row={row}
              onText={() => onOpenContact(row)}
              onSnooze={(until) => onSnooze(row, until)}
              onDone={() => onDone(row)}
              onOpenContact={() => onOpenContact(row)}
            />
          </div>

          {/* The side arrows are md and up; below that these carry the same two. */}
          <div className="mt-3 flex shrink-0 items-center justify-between md:hidden">
            <button
              type="button"
              onClick={() => previous && onGo(previous.id)}
              disabled={!previous}
              className="inline-flex items-center gap-1 text-body-sm text-subtle transition hover:text-default disabled:opacity-40"
            >
              <ArrowLeftIcon size={14} weight="bold" /> Previous
            </button>
            <button
              type="button"
              onClick={() => next && onGo(next.id)}
              disabled={!next}
              className="inline-flex items-center gap-1 text-body-sm text-subtle transition hover:text-strong disabled:opacity-40"
            >
              Next <ArrowRightIcon size={14} weight="bold" />
            </button>
          </div>
        </div>

        <NavArrow direction="next" onClick={() => next && onGo(next.id)} disabled={!next} />
      </DialogContent>
    </Dialog>
  )
}
